/**
 * Purpose: Provides message action handlers for operations like delete, copy, react
 *
 * How it works:
 * 1. Centralizes all message action logic in one place
 * 2. Handles API calls and error states
 * 3. Provides toast feedback for user actions
 *
 * Key features:
 * - Message deletion (server and local)
 * - Text copying with HTML/plain text support
 * - Message reaction handling
 */

import { MessageEntity } from "@/indexedDB/types";
import { toast } from "@/hooks/useToast";
import { copyHtmlToClipboard } from "@/lib/clipboard";
import { deleteMessageFromChat, togglePinMessage } from "@/middleware/messages";
import { removeMessageByClientIdFromLocalDB } from "@/indexedDB/services/messages";
import { useTranslation } from "react-i18next";
import { umbra_addReactionToMessage } from "@/middleware/umbra_reactions";

export function useMessageActions() {
  const { t } = useTranslation();
  /**
   * Handles adding a reaction to a message
   */
  const handleReactToMessage = async (
    message: MessageEntity,
    reaction: string,
  ) => {
    try {
      await umbra_addReactionToMessage({
        chatId: message.chatId,
        messageId: message.id,
        emoji: reaction,
      });
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  /**
   * Handles deleting a message from the server
   */
  const handleDeleteMessage = async (id: string) => {
    try {
      await deleteMessageFromChat({
        messageId: id,
      });
      toast({
        duration: 1000,
        description: t("message.deleted"),
      });
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  /**
   * Handles deleting a message from local database only
   */
  const handleDeleteMessageFromLocalDB = async (id: string) => {
    try {
      await removeMessageByClientIdFromLocalDB(id);
      toast({
        duration: 1000,
        description: t("message.removed"),
      });
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  /**
   * Handles copying text to clipboard with HTML/plain text support
   */
  const handleCopyTextToClipboard = async (text: string) => {
    if (!text) {
      return;
    }

    const copied = await copyHtmlToClipboard(text);

    toast(
      copied
        ? { duration: 1000, description: t("copied"), variant: "default" }
        : {
            duration: 1000,
            description: t("couldNotCopyText"),
            variant: "destructive",
          },
    );
  };

  const handleTogglePinMessage = async (message: MessageEntity) => {
    const payload = {
      id: message.id,
      chat_id: message.chatId,
      sent_at: message.timestamp,
      category: message.category,
    };
    try {
      await togglePinMessage(payload);
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  return {
    handleReactToMessage,
    handleDeleteMessage,
    handleDeleteMessageFromLocalDB,
    handleCopyTextToClipboard,
    handleTogglePinMessage,
  };
}
